import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, Rocket, Users, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const iconMap = {
  GraduationCap,
  Rocket,
  Users,
};

const linkMap = {
  'trainings': '/egitimler',
  'projects': '/projeler',
  'events': '/etkinlikler',
};

const FeaturesSection = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const featuresConfig = [
    { id: 1, icon: 'GraduationCap', key: 'trainings' },
    { id: 2, icon: 'Rocket', key: 'projects' },
    { id: 3, icon: 'Users', key: 'events' },
  ];

  const handleCardClick = (key) => {
    const path = linkMap[key];
    if (path) {
      navigate(path);
      window.scrollTo(0, 0);
    }
  };

  return (
    <section id="features" className="relative py-24 bg-gradient-to-b from-[#060b1a] to-[#0a1628]">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-[#4DFF88]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#4DD6FF]/10 rounded-full blur-[180px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 rounded-full border border-[#4DD6FF]/30 bg-[#4DD6FF]/5 text-[#4DD6FF] text-sm font-medium mb-4">
            {t('features.badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4">
            {t('features.title')}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('features.subtitle')}
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {featuresConfig.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            const hasLink = linkMap[feature.key];
            return (
              <div
                key={feature.id}
                className={`group relative ${hasLink ? 'cursor-pointer' : ''}`}
                style={{ animationDelay: `${index * 100}ms` }}
                onClick={() => hasLink && handleCardClick(feature.key)}
              >
                {/* Glowing border effect */}
                <div className="absolute -inset-px rounded-xl bg-gradient-to-br from-[#4DFF88]/50 via-[#4DD6FF]/30 to-[#FF4DA6]/50 opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-500" />
                
                {/* Card */}
                <div className="relative h-full p-8 rounded-xl border border-white/10 bg-[#0a1628]/80 backdrop-blur-sm transition-all duration-500 group-hover:border-transparent">
                  {/* Icon */}
                  <div className="mb-6 inline-flex items-center justify-center w-14 h-14 rounded-lg bg-gradient-to-br from-[#4DFF88]/20 to-[#4DD6FF]/10 text-[#4DFF88] group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-7 h-7" />
                  </div>

                  {/* Title with arrow */}
                  <h3 className="text-xl font-semibold text-white mb-4 group-hover:text-[#4DFF88] transition-colors duration-300 flex items-center gap-2">
                    {t(`features.${feature.key}.title`)}
                    {hasLink && (
                      <ArrowRight className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    )}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-400 leading-relaxed">
                    {t(`features.${feature.key}.description`)}
                  </p>

                  {/* Decorative line */}
                  <div className="absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#4DFF88]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
